import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { webStorage } from 'sidecar-utility-library'

import { isImpersonating } from 'pages/App/utils/handleImpersonatingAdmin'
import { selectAuthToken } from 'store/Auth/selectors'

/**
 * Reloads this tab when another tab signs in, out, or as someone else.
 *
 * The token lives in localStorage, which every tab shares, but each tab's Redux
 * store is its own. Without this, a tab left open keeps rendering the member it
 * booted with after the shared token has gone or changed, and builds new
 * requests from whichever token is stored now. A reload boots from storage
 * again: signed out lands on /login, signed in as the stored member.
 *
 * Except on /logout: a reload there would mount Logout with the new token and
 * revoke it, so that tab goes to the dashboard instead.
 */
const isSharedAdminFlagSet = () =>
  String(webStorage.getItem('isAdmin')) === 'true'

const isOtherTabImpersonating = () =>
  isSharedAdminFlagSet() && !sessionStorage.getItem('isAdmin')

const isLogoutPage = () => window.location.pathname.startsWith('/logout')

export const useCrossTabAuthSync = () => {
  const authToken = useSelector(selectAuthToken)
  const authTokenRef = useRef(authToken)

  useEffect(() => {
    authTokenRef.current = authToken
  }, [authToken])

  useEffect(() => {
    // `key` is null when a tab clears storage wholesale on logout.
    const onStorage = event => {
      if (event.key !== 'token' && event.key !== null) return
      const storedToken = webStorage.getItem('token') ?? null
      if (storedToken === (authTokenRef.current ?? null)) return
      // A member signed in over this tab's impersonation, so it is over. Left
      // set, the reload below would re-assert `isAdmin` and every other tab
      // would wipe the member's new token. A sibling impersonation sets the
      // shared flag before its token, so when it is set this tab follows along.
      if (isImpersonating() && !isSharedAdminFlagSet())
        sessionStorage.removeItem('isAdmin')
      // Another tab started impersonating a member. This tab isn't part of
      // that session, and booting here would end it: `setImpersonationStorage`
      // wipes an admin token from any tab whose sessionStorage lacks `isAdmin`.
      if (storedToken && isOtherTabImpersonating()) return
      // Reloading /logout would mount Logout with the new token and revoke it.
      // Same destination as AuthGuard: `next` belonged to the previous member.
      if (storedToken && isLogoutPage()) {
        window.location.assign('/dashboard?splash=false')
        return
      }
      window.location.reload()
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])
}
